import type { LootRefusalReason } from "odyssey-shared";

/**
 * A chest's lines as the page holds them (P2.8-T16, ADR-091 decisions 19 and 22): pure rules over plain data, which
 * the chest store runs on. The page holds only what the server said. A `loot` replaces everything it holds of a
 * chest; a take marks the lines it names pending and changes no count and no taken flag; only a `lootTaken` marks a
 * line taken, and each of its refusal reasons does what the map server means by it (ADR-091's note of 2026-10-05,
 * item 8):
 *
 * - `full`: the line stays, marked full and not offered again until the bag changes, since nothing else can make
 *   room for it;
 * - `taken`: the line is taken, or is none of the chest's, so it leaves the list;
 * - `closed` and `reach`: the session no longer holds the chest's lines, or the character no longer reaches the
 *   chest, so the window closes;
 * - `busy` and `unavailable`: nothing moved and the line may be asked for again; after `unavailable` the map server
 *   sends the chest's lines afresh.
 */

/**
 * A line as `loot` carries it: its number, its item's rules id, its quantity and whether it is taken.
 */
export type WireLine = readonly [line: number, item: string, quantity: number, taken: boolean];

/**
 * One line of a chest: what the server said of it, and the page's two marks: `pending` while a take that names it
 * waits for its answer, and `full` from a refusal for want of room until the bag changes.
 */
export interface ChestLine {
  readonly line: number;
  readonly item: string;
  readonly quantity: number;
  readonly taken: boolean;
  readonly pending: boolean;
  readonly full: boolean;
}

/**
 * A chest the page holds the lines of: its handle in the session's view, and its lines in line order.
 */
export interface Chest {
  readonly handle: number;
  readonly lines: readonly ChestLine[];
}

/**
 * What the window shows of a chest: its rows; "Nothing left to take." for a roll that held nothing; or "You have
 * already looted this." once every line is taken.
 */
export type ChestShown = "available" | "empty" | "looted";

/**
 * What one line taken gave the bag.
 */
export interface Gain {
  readonly item: string;
  readonly quantity: number;
}

/**
 * What a `lootTaken` did: the chest after it, or null when it closes the window; what each line it took gave; and
 * each refusal reason it carried, once, the ones that close the window first.
 */
export interface Answer {
  readonly chest: Chest | null;
  readonly gained: readonly Gain[];
  readonly reasons: readonly LootRefusalReason[];
}

/**
 * The order refusal reasons are reported in: what closes the window, then what the player can act on, then the rest.
 */
const REASON_ORDER: readonly LootRefusalReason[] = ["closed", "reach", "full", "busy", "unavailable", "taken"];

/**
 * A chest as a `loot` describes it.
 *
 * @param handle The chest's handle.
 * @param lines The lines the message carries.
 * @param before What the page held when the message came, or null for nothing. A line still untaken keeps the marks
 *   it had when `before` is the same chest, so opening a chest again while a take waits clears no wait.
 * @returns The chest, its lines in line order.
 * @example
 * openChest(7, [[0, "minor_healing_potion", 2, false]]);
 * // { handle: 7, lines: [{ line: 0, item: "minor_healing_potion", quantity: 2, taken: false, pending: false, full: false }] }
 */
export function openChest(handle: number, lines: readonly WireLine[], before: Chest | null = null): Chest {
  const kept = before !== null && before.handle === handle ? before.lines : [];
  return {
    handle,
    lines: lines
      .map(([line, item, quantity, taken]): ChestLine => {
        const was = taken ? undefined : kept.find((each) => each.line === line && !each.taken);
        return { line, item, quantity, taken, pending: was?.pending ?? false, full: was?.full ?? false };
      })
      .sort((a, b) => a.line - b.line),
  };
}

/**
 * What the window shows of a chest.
 *
 * @param chest The chest.
 * @returns `empty` for no line at all, `looted` when every line is taken, `available` otherwise.
 */
export function shownOf(chest: Chest): ChestShown {
  if (chest.lines.length === 0) {
    return "empty";
  }
  return chest.lines.every((each) => each.taken) ? "looted" : "available";
}

/**
 * The lines the window draws a row for.
 *
 * @param chest The chest.
 * @returns Its untaken lines, in line order.
 */
export function untaken(chest: Chest): readonly ChestLine[] {
  return chest.lines.filter((each) => !each.taken);
}

/**
 * The lines a take may name now: untaken, with no take waiting on them, and not refused for want of room since the
 * bag last changed.
 *
 * @param chest The chest.
 * @param wanted The lines wished for; every line of the chest when omitted, as Take all wishes.
 * @returns The line numbers, in line order.
 * @example
 * askable(openChest(7, [[0, "minor_healing_potion", 2, true], [1, "minor_healing_potion", 1, false]]));
 * // [1]
 */
export function askable(chest: Chest, wanted?: readonly number[]): number[] {
  return chest.lines.filter((each) => !each.taken && !each.pending && !each.full && (wanted === undefined || wanted.includes(each.line))).map((each) => each.line);
}

/**
 * A chest with each line a function changes replaced, and the same chest when the function changes none.
 *
 * @param chest The chest.
 * @param change Returns a line's replacement, or the line itself to keep it.
 * @returns The chest after.
 */
function withLines(chest: Chest, change: (line: ChestLine) => ChestLine): Chest {
  let changed = false;
  const lines = chest.lines.map((each) => {
    const next = change(each);
    changed ||= next !== each;
    return next;
  });
  return changed ? { handle: chest.handle, lines } : chest;
}

/**
 * A chest after a take was sent: the lines it names wait for the answer.
 *
 * @param chest The chest.
 * @param lines The lines the take names.
 * @returns The chest with those lines pending.
 */
export function markAsked(chest: Chest, lines: readonly number[]): Chest {
  return withLines(chest, (each) => (lines.includes(each.line) && !each.pending ? { ...each, pending: true } : each));
}

/**
 * A chest after a take went unanswered for too long: the lines lose their pending mark and nothing else.
 *
 * @param chest The chest.
 * @param lines The lines whose wait ended.
 * @returns The chest after, the same chest when none of them was pending.
 */
export function settleLines(chest: Chest, lines: readonly number[]): Chest {
  return withLines(chest, (each) => (lines.includes(each.line) && each.pending ? { ...each, pending: false } : each));
}

/**
 * A chest after the bag changed: a line refused for want of room may be asked for again.
 *
 * @param chest The chest.
 * @returns The chest with no line marked full, the same chest when none was.
 */
export function roomMade(chest: Chest): Chest {
  return withLines(chest, (each) => (each.full ? { ...each, full: false } : each));
}

/**
 * Applies a `lootTaken` to the chest it names.
 *
 * @param chest The chest.
 * @param taken The lines now in the bag.
 * @param refused The lines refused, each with its reason.
 * @returns The chest after, null when a refusal closes the window; what each line newly taken gave; and the reasons.
 * @example
 * applyAnswer(chest, [0], [[1, "full"]]);
 * // { chest: <line 0 taken, line 1 full>, gained: [{ item: "minor_healing_potion", quantity: 2 }], reasons: ["full"] }
 */
export function applyAnswer(chest: Chest, taken: readonly number[], refused: readonly (readonly [line: number, reason: LootRefusalReason])[]): Answer {
  const gained: Gain[] = [];
  const reasonOf = new Map(refused);
  const named = new Set(refused.map(([, reason]) => reason));
  const after = withLines(chest, (each) => {
    if (taken.includes(each.line)) {
      if (each.taken) {
        return each;
      }
      gained.push({ item: each.item, quantity: each.quantity });
      return { ...each, taken: true, pending: false, full: false };
    }
    const reason = reasonOf.get(each.line);
    if (reason === undefined) {
      return each;
    }
    if (reason === "taken") {
      return each.taken ? each : { ...each, taken: true, pending: false, full: false };
    }
    if (reason === "full") {
      return each.taken ? each : { ...each, pending: false, full: true };
    }
    return each.pending ? { ...each, pending: false } : each;
  });
  return { chest: named.has("closed") || named.has("reach") ? null : after, gained, reasons: REASON_ORDER.filter((reason) => named.has(reason)) };
}
